import * as Aria from "react-aria-components"
import styled from "styled-components"
import { ArrowDropDownIcon } from "../icons/ArrowDropDownIcon"
import { ArrowDropUpIcon } from "../icons/ArrowDropUpIcon"
import { CheckmarkIcon } from "../icons/CheckboxIcons"
import H3 from "../typography/H3"

export interface Option<T = undefined> {
  id: Aria.Key
  name: string
  value?: T
}

interface DropdownProps<T> {
  name?: string
  label: string
  disabled?: boolean
  placeholder?: string
  options: Option<T>[]
  onSelectionChange?: (id: Aria.Key) => void
  selectedKey?: Aria.Key
}

const SelectStyle = styled(Aria.Select)`
  position: relative;
  color: ${({ theme }) => theme.colors.greyDark};

  &:focus-within {
    outline: auto;
  }

  .arrow-drop-down,
  .arrow-drop-up {
    position: absolute;
    right: 0;
  }

  &[data-open] .arrow-drop-down,
  .arrow-drop-up {
    display: none;
  }

  &[data-open] .arrow-drop-up,
  .arrow-drop-down {
    display: unset;
  }

  &[data-disabled] {
    border-color: ${({ theme }) => theme.colors.greyDisabled};
  }
`

const ButtonStyle = styled(Aria.Button)`
  background: none;
  border: none;
  outline: none;

  border-bottom: solid 3px ${({ theme }) => theme.colors.greyDark};

  color: ${({ theme }) => theme.colors.greyDark};
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  column-gap: 2rem;
  padding-bottom: 6px;
  width: 100%;
  padding-right: 3rem;

  white-space: nowrap;

  &[data-disabled] {
    cursor: auto;
    color: ${({ theme }) => theme.colors.greyDisabled};
  }
`

const PopoverStyle = styled(Aria.Popover)`
  outline: none;
  box-shadow: 0px 2px 6px 0px rgba(119, 119, 119, 0.25);
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 0px 0px 5px 5px;
  width: var(--trigger-width);
  padding: 0.5rem;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.greyDark};

  & .react-aria-ListBox {
    outline: none;
    display: grid;
    row-gap: 1rem;
  }
`

const ListBoxItemStyle = styled(Aria.ListBoxItem)`
  display: flex;
  align-items: center;
  column-gap: 0.25rem;
  cursor: pointer;

  &[data-disabled] {
    cursor: auto;
  }

  & .checkmark {
    visibility: hidden;
  }

  &[data-selected] .checkmark {
    visibility: visible;
  }
`

export const optionsMap = <T,>(options: Option<T>[]) =>
  Object.fromEntries(options.map(({ id, ...props }) => [id, props]))

const Dropdown = <T,>({
  label,
  name,
  options,
  disabled,
  placeholder,
  onSelectionChange,
  selectedKey,
}: DropdownProps<T>) => {
  const renderSelectedValue = ({ selectedItem }: Aria.SelectValueRenderProps<Option<T>>) => selectedItem?.name

  return (
    <SelectStyle
      name={name}
      isDisabled={disabled}
      aria-label={label}
      placeholder={placeholder}
      onSelectionChange={onSelectionChange}
      selectedKey={selectedKey}
    >
      <ButtonStyle>
        <H3 color="inherit">
          <Aria.SelectValue>{renderSelectedValue}</Aria.SelectValue>
        </H3>
        <ArrowDropDownIcon color="currentColor" />
        <ArrowDropUpIcon color="currentColor" />
      </ButtonStyle>
      <PopoverStyle offset={4}>
        <Aria.ListBox items={options}>
          {(option) => (
            <ListBoxItemStyle key={option.id} id={option.id} textValue={option.name}>
              <CheckmarkIcon />
              <H3 color="inherit">{option.name}</H3>
            </ListBoxItemStyle>
          )}
        </Aria.ListBox>
      </PopoverStyle>
    </SelectStyle>
  )
}

export default Dropdown
